import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { evaluateFormula, evaluatePredicate } from '@workspace/formulas';
import type { FormulaContext, ScalarValue } from '@workspace/formulas';
import { fetchLookupOptions } from './formTemplatesApi';
import type { FormField } from '../surveys/types';

const LOOKUP_CACHE_TTL_MS = 60_000;

interface LookupCacheEntry {
  expiresAt: number;
  options: Array<{ value: unknown; label: string; responseId: string }>;
}

type LookupKey = `${string}|${string}|${string}`;
function makeKey(templateCode: string, valueField: string, displayField: string): LookupKey {
  return `${templateCode}|${valueField}|${displayField}`;
}

interface UseFormulaEngineArgs {
  fields: FormField[];
  values: Record<string, unknown>;
}

interface UseFormulaEngineResult {
  /** Mapa fieldId → valor calculado en vivo. */
  calculated: Record<string, ScalarValue>;
  /** Set de fieldIds que están visibles según `visibleIf`. */
  visibleSet: Set<string>;
  /** Set de fieldIds que están "required" en este momento (incluye `required` estático + `requiredIf`). */
  requiredSet: Set<string>;
  /**
   * Resolver de opciones de lookup (con cache simple y debounce externo).
   * Devuelve la lista actualmente cargada para la combinación; lanza
   * `loadLookup` para forzar carga / refresh.
   */
  getLookupOptions: (templateCode: string, valueField: string, displayField: string) => LookupCacheEntry['options'];
  loadLookup: (templateCode: string, valueField: string, displayField: string, search?: string) => Promise<void>;
}

/**
 * Motor reutilizable de evaluación reactiva para el renderer de formularios.
 * - Calcula campos `calculated` de forma síncrona.
 * - Determina visibilidad (`visibleIf`) y obligatoriedad (`requiredIf`).
 * - Expone API para cargar opciones de `lookup`/`reference` con cache 60s.
 */
export function useFormulaEngine({ fields, values }: UseFormulaEngineArgs): UseFormulaEngineResult {
  const [lookupCache, setLookupCache] = useState<Map<LookupKey, LookupCacheEntry>>(() => new Map());
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const calculatedFields = useMemo(
    () => fields.filter((f) => f.type === 'calculated' && f.formula),
    [fields],
  );

  const ctx: FormulaContext = useMemo(
    () => ({
      getField: (id: string) => {
        const v = valuesRef.current[id];
        if (v === undefined) return undefined;
        // Conversión simple a ScalarValue
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || v === null) return v;
        if (v instanceof Date) return v;
        return String(v);
      },
    }),
    [],
  );

  const calculated = useMemo(() => {
    const out: Record<string, ScalarValue> = {};
    const localCtx: FormulaContext = {
      getField: (id: string) => {
        const v = Object.prototype.hasOwnProperty.call(out, id) ? out[id] : valuesRef.current[id];
        if (v === undefined) return undefined;
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || v === null) return v;
        if (v instanceof Date) return v;
        return String(v);
      },
    };

    for (let pass = 0; pass < Math.max(1, calculatedFields.length); pass++) {
      for (const field of calculatedFields) {
        try {
          out[field.id] = evaluateFormula(field.formula ?? '', localCtx).value;
        } catch {
          out[field.id] = null;
        }
      }
    }
    return out;
    // Recalcular cuando cambien los valores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, calculatedFields, ctx]);

  const visibleSet = useMemo(() => {
    const visible = new Set<string>();
    for (const f of fields) {
      if (!f.visibleIf || evaluatePredicate(f.visibleIf, ctx)) {
        visible.add(f.id);
      }
    }
    return visible;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, values, ctx]);

  const requiredSet = useMemo(() => {
    const required = new Set<string>();
    for (const f of fields) {
      if (f.required) {
        required.add(f.id);
        continue;
      }
      if (f.requiredIf && evaluatePredicate(f.requiredIf, ctx)) {
        required.add(f.id);
      }
    }
    return required;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, values, ctx]);

  const loadLookup = useCallback(
    async (templateCode: string, valueField: string, displayField: string, search?: string) => {
      const key = makeKey(templateCode, valueField, displayField);
      const cached = lookupCache.get(key);
      // Si hay cache fresca y no hay search, no recargar.
      if (cached && cached.expiresAt > Date.now() && !search) return;
      try {
        const options = await fetchLookupOptions({ templateCode, valueField, displayField, search });
        setLookupCache((prev) => {
          const next = new Map(prev);
          next.set(key, { expiresAt: Date.now() + LOOKUP_CACHE_TTL_MS, options });
          return next;
        });
      } catch {
        setLookupCache((prev) => {
          const next = new Map(prev);
          next.set(key, { expiresAt: Date.now() + LOOKUP_CACHE_TTL_MS, options: [] });
          return next;
        });
      }
    },
    [lookupCache],
  );

  const getLookupOptions = useCallback(
    (templateCode: string, valueField: string, displayField: string) => {
      const key = makeKey(templateCode, valueField, displayField);
      return lookupCache.get(key)?.options ?? [];
    },
    [lookupCache],
  );

  // Carga inicial perezosa de todos los lookups detectados en la estructura.
  useEffect(() => {
    const lookups = fields.filter(
      (f) => (f.type === 'lookup' || f.type === 'reference') && f.source?.templateCode,
    );
    for (const f of lookups) {
      const s = f.source!;
      void loadLookup(s.templateCode, s.valueField, s.displayField);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  return { calculated, visibleSet, requiredSet, getLookupOptions, loadLookup };
}
